import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import type { Components } from 'react-markdown';
import type { Element } from 'hast';
import { CodeBlock } from './CodeBlock';
import { cn } from '@/lib/utils';

/**
 * Resolves a relative path against a base path
 * @param basePath - Current document path (e.g., "getting-started/README")
 * @param relativePath - Relative path to resolve (e.g., "../core-concepts/organizations")
 * @returns Resolved absolute path
 */
function resolveDocPath(basePath: string | undefined, relativePath: string): string {
  // If no base path or relative path doesn't start with ./ or ../, return as-is
  if (!basePath || (!relativePath.startsWith('./') && !relativePath.startsWith('../'))) {
    return relativePath;
  }

  // Split paths into segments
  const baseSegments = basePath.split('/').slice(0, -1); // Remove filename, keep directory
  const relativeSegments = relativePath.split('/');

  // Process each segment of the relative path
  for (const segment of relativeSegments) {
    if (segment === '..') {
      baseSegments.pop(); // Go up one directory
    } else if (segment !== '.' && segment !== '') {
      baseSegments.push(segment); // Add segment
    }
  }

  return baseSegments.join('/');
}

/**
 * Props for the MarkdownRenderer component
 */
interface MarkdownRendererProps {
  /** The markdown content to render */
  content: string;
  /** Additional CSS class names */
  className?: string;
  /** Current document path for resolving relative links */
  currentPath?: string;
}

/**
 * Custom paragraph renderer to prevent nesting issues with code blocks
 */
function CustomParagraph({ node, children, ...props }: Parameters<Components['p']>[0]) {
  // Check if the paragraph contains a code block (which would be invalid HTML nesting)
  const hasCodeBlock = node?.children?.some(
    (child): child is Element =>
      child.type === 'element' &&
      child.tagName === 'code' &&
      (!!child.properties?.className || child.children?.length > 0),
  );

  // If it contains a code block, render children without <p> wrapper to avoid nesting issues
  // React Markdown will handle the spacing
  if (hasCodeBlock) {
    return children as React.ReactElement;
  }

  // Otherwise, render as normal paragraph
  return <p {...props}>{children}</p>;
}

/**
 * Custom code block renderer with syntax highlighting
 */
function CustomCode(props: Parameters<Components['code']>[0]) {
  const { className, children, inline, ...rest } = props as {
    className?: string;
    children?: React.ReactNode;
    inline?: boolean;
  };

  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : undefined;

  // Block-level code (not inline)
  if (!inline) {
    return (
      <CodeBlock language={language} className="my-4">
        {String(children).replace(/\n$/, '')}
      </CodeBlock>
    );
  }

  // Inline code
  return (
    <code className={className} {...rest}>
      {children}
    </code>
  );
}

/**
 * Custom image renderer to handle errors gracefully
 */
function CustomImage({ ...props }: Parameters<Components['img']>[0]) {
  return (
    <img
      {...props}
      alt={props.alt || ''}
      loading="lazy"
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        target.style.display = 'none';
        // Silently fail for broken images
      }}
    />
  );
}

/**
 * Creates a custom link renderer for internal/external links with path resolution
 */
function createCustomLink(currentPath?: string) {
  return function CustomLink({ children, href, ...props }: Parameters<Components['a']>[0]) {
    const isExternal = href?.startsWith('http');
    const isAnchor = href?.startsWith('#');

    // Strip .md extension from internal documentation links
    let processedHref = href;
    if (!isExternal && !isAnchor && href) {
      if (href.endsWith('.md')) {
        processedHref = href.slice(0, -3);
      }

      // Resolve relative paths (../ or ./) against current document path
      if (processedHref && (processedHref.startsWith('../') || processedHref.startsWith('./'))) {
        processedHref = resolveDocPath(currentPath, processedHref);
      }

      // Convert relative documentation links to absolute paths
      // This ensures links work regardless of trailing slash on current URL
      if (processedHref && !processedHref.startsWith('/')) {
        processedHref = `/documentation/${processedHref}`;
      }
    }

    return (
      <a
        {...props}
        href={processedHref}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        onClick={
          isAnchor && href
            ? (e) => {
                e.preventDefault();
                // Remove the # and use getElementById to avoid selector issues with IDs starting with numbers
                const id = href.slice(1);
                const element = document.getElementById(id);
                element?.scrollIntoView({ behavior: 'smooth' });
              }
            : undefined
        }
      >
        {children}
      </a>
    );
  };
}

/**
 * Custom table wrapper for responsive tables
 */
function CustomTable({ children, ...props }: Parameters<Components['table']>[0]) {
  return (
    <div className="overflow-x-auto my-6">
      <table {...props}>{children}</table>
    </div>
  );
}

/**
 * MarkdownRenderer component for rendering markdown content with custom styling
 *
 * @description
 * A comprehensive markdown renderer that converts markdown content into
 * styled HTML with syntax highlighting, tables, task lists, and more.
 *
 * Features:
 * - GitHub Flavored Markdown (GFM) support
 * - Syntax highlighting via CodeBlock component
 * - Table support with styling
 * - Task list support
 * - Image handling
 * - Link styling
 * - Tailwind typography integration
 *
 * Uses:
 * - react-markdown for rendering
 * - remark-gfm for GitHub Flavored Markdown
 * - rehype-slug for heading IDs
 *
 * @example
 * ```tsx
 * function DocumentPage() {
 *   const markdown = "# Hello World\nThis is **bold** text.";
 *   return <MarkdownRenderer content={markdown} />;
 * }
 * ```
 */
export function MarkdownRenderer({ content, className, currentPath }: MarkdownRendererProps) {
  return (
    <div
      className={cn(
        'prose prose-neutral dark:prose-invert max-w-none',
        // Headings
        'prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-primary',
        'prose-headings:scroll-mt-24', // Add scroll margin for sticky header
        'prose-h1:text-3xl prose-h1:mb-4 prose-h1:mt-6',
        'prose-h2:text-2xl prose-h2:mb-3 prose-h2:mt-8 prose-h2:pb-2 prose-h2:border-b',
        'prose-h3:text-xl prose-h3:mb-2 prose-h3:mt-6',
        'prose-h4:text-lg prose-h4:mb-2 prose-h4:mt-4',
        // Paragraphs
        'prose-p:leading-7 prose-p:mb-4',
        // Lists
        'prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6',
        'prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6',
        'prose-li:my-1',
        // Links
        'prose-a:text-primary prose-a:no-underline hover:prose-a:underline',
        // Code
        'prose-code:text-sm prose-code:font-mono',
        'prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded',
        'prose-code:bg-gray-100 dark:prose-code:bg-gray-800',
        'prose-code:before:content-none prose-code:after:content-none',
        // Blockquotes
        'prose-blockquote:border-l-4 prose-blockquote:border-primary',
        'prose-blockquote:pl-4 prose-blockquote:italic',
        'prose-blockquote:text-gray-700 dark:prose-blockquote:text-gray-300',
        // Tables
        'prose-table:border-collapse prose-table:w-full',
        'prose-thead:border-b-2',
        'prose-th:p-3 prose-th:text-left prose-th:font-semibold',
        'prose-td:p-3 prose-td:border-t',
        // Images
        'prose-img:rounded-lg prose-img:shadow-md',
        // Horizontal rules
        'prose-hr:my-8 prose-hr:border-gray-300 dark:prose-hr:border-gray-700',
        // Strong/Bold
        'prose-strong:font-semibold',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSlug]}
        components={{
          p: CustomParagraph,
          code: CustomCode,
          img: CustomImage,
          a: createCustomLink(currentPath),
          table: CustomTable,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
